import { useEffect, useRef, useState } from 'react';
import Select, { ActionMeta, components, OnChangeValue } from 'react-select';

import { getStylesConfig } from './stylesConfig';
export interface IOption {
	label: string | number;
	value?: string | number;
}
export type Size = 'medium' | 'large';

export type FlexWrap = 'nowrap' | 'wrap' | 'wrap-reverse';

export interface IStyles {
	container: {
		maxWidth?: string;
	};
	valueContainer: {
		flexWrap?: FlexWrap;
	};
	multiValue: {
		minWidth?: string;
	};
}

interface IMultiSelectProps {
	options: IOption[];
	placeholder?: string;
	size: Size;
	value?: IOption[];
	defaultValue?: any;
	notHideSelectedOptions?: boolean;
	selectAllTitle?: string;
	isLoading?: boolean;
	styles?: Partial<IStyles>;
	triggerReset?: any;
	closeMenuOnScroll?: boolean;
	blurInputOnSelect?: boolean;
	onChange: (
		newValue: OnChangeValue<IOption, true>,
		actionMeta: ActionMeta<IOption>
	) => void;
	id?: string;
	'data-test'?: string;
}
export default function MultiSelect({
	options,
	placeholder,
	size,
	value,
	defaultValue,
	notHideSelectedOptions,
	selectAllTitle,
	isLoading,
	styles,
	triggerReset,
	onChange,
	id,
	'data-test': dataTest,
}: IMultiSelectProps) {
	const formatedOptions = options.map(({ label, value }) => ({
		value: value || label,
		label,
	}));

	const valueRef = useRef(value);
	valueRef.current = value;

	const inputRef = useRef(null);

	const selectAllOption = {
		value: 'all',
		label: selectAllTitle,
	};

	const [menuIsOpen, setMenuIsOpen] = useState(false);

	useEffect(() => {
		if (triggerReset) {
			if (inputRef.current != null) {
				const selectInput = inputRef.current;
				// @ts-ignore
				selectInput.clearValue();
			}
		}
	}, [triggerReset]);

	const isSelectAllSelected = () => {
		return (
			value?.length === formatedOptions.length &&
			formatedOptions.length > 0
		);
	};

	const isOptionSelected = option => {
		return (
			valueRef?.current?.some(({ value }) => value === option.value) ||
			isSelectAllSelected()
		);
	};

	const getOptions = () =>
		!isLoading
			? !!selectAllTitle && options.length
				? [selectAllOption, ...formatedOptions]
				: formatedOptions
			: [];

	const getValue = () => {
		if (selectAllTitle && isSelectAllSelected()) {
			return [selectAllOption];
		}
		return value;
	};

	const handleChange = (newValue, actionMeta) => {
		const { action, option, removedValue } = actionMeta;

		if (
			action === 'select-option' &&
			option.value === selectAllOption.value
		) {
			onChange(formatedOptions, actionMeta);
		} else if (
			(action === 'deselect-option' &&
				option.value === selectAllOption.value) ||
			(action === 'remove-value' &&
				removedValue.value === selectAllOption.value)
		) {
			onChange([], actionMeta);
		} else if (action === 'deselect-option' && isSelectAllSelected()) {
			onChange(
				formatedOptions.filter(({ value }) => value !== option.value),
				actionMeta
			);
		} else {
			onChange(newValue || [], actionMeta);
		}
	};

	const Option = props => {
		const index = props?.data.label;
		const newInnerProps = {
			...props.innerProps,
			'data-test': `option-${dataTest}-${index}`,
		};
		return (
			<components.Option
				{...props}
				innerProps={newInnerProps}
			/>
		);
	};

	return (
		<Select
			components={{
				Option,
				Input: inputProps => (
					<components.Input
						{...inputProps}
						data-test={dataTest}
					/>
				),
			}}
			isMulti
			isLoading={isLoading}
			hideSelectedOptions={!notHideSelectedOptions}
			placeholder={placeholder}
			defaultValue={defaultValue}
			closeMenuOnSelect={false}
			closeMenuOnScroll={true}
			blurInputOnSelect={true}
			value={getValue()}
			styles={getStylesConfig(size, styles)}
			options={getOptions()}
			onChange={handleChange}
			isOptionSelected={selectAllTitle ? isOptionSelected : undefined}
			onMenuOpen={() => setMenuIsOpen(true)}
			onMenuClose={() => setMenuIsOpen(false)}
			onBlur={() => setMenuIsOpen(false)}
			menuIsOpen={menuIsOpen}
			ref={inputRef}
			id={id}
		/>
	);
}
